import { GAME_CONFIG, type GameConfig } from './GameConfig';
import { CASE_STATUS_VALUES } from './types';
import type {
  BacklogEntry,
  CaseStatus,
  GameProgressHydration,
  GameSettingsHydration,
  GameProgress,
  GameSettings,
  GameStateHydration,
  GameStateSnapshot,
  ScenarioLine,
  ScenarioResumePoint
} from './types';

const CASE_STATUSES: ReadonlySet<CaseStatus> = new Set(CASE_STATUS_VALUES);
const MAX_BACKLOG_ENTRIES = 200;
const GAME_PROGRESS_SCHEMA_VERSION = 2;

export const DEFAULT_SETTINGS: Readonly<GameSettings> = {
  masterVolume: 1,
  bgmVolume: 0.72,
  seVolume: 0.72,
  voiceVolume: 0.75,
  textSpeed: 0.55,
  autoSpeed: 0.65,
  skipReadText: true,
  reduceMotion: false,
  lightweightEffects: false,
  screenShake: true,
  visualEffects: true
};

export class GameState {
  private progress: GameProgress;
  private settings: GameSettings;

  public constructor(config: GameConfig = GAME_CONFIG, snapshot?: GameStateHydration) {
    this.progress = createInitialProgress(config);
    this.settings = { ...DEFAULT_SETTINGS };

    if (snapshot) {
      this.hydrate(snapshot);
    }
  }

  public getSnapshot(): GameStateSnapshot {
    return {
      progress: cloneProgress(this.progress),
      settings: { ...this.settings }
    };
  }

  public getProgress(): GameProgress {
    return cloneProgress(this.progress);
  }

  public getSettings(): GameSettings {
    return { ...this.settings };
  }

  public setScenario(scenarioId: string): void {
    this.progress.currentScenarioId = scenarioId;
    this.progress.resumePoint = {
      scenarioId,
      nodeId: scenarioId,
      lineIndex: 0
    };
  }

  public startScenario(scenarioId: string, caseId: string, nodeId: string): void {
    this.progress.currentScenarioId = scenarioId;
    this.progress.currentCaseId = caseId;
    addUnique(this.progress.unlockedCaseIds, caseId);
    if (this.getCaseStatus(caseId) === 'hidden') {
      this.progress.caseStatuses[caseId] = 'investigating';
    }
    this.progress.resumePoint = {
      scenarioId,
      nodeId,
      lineIndex: 0
    };
  }

  public setResumePoint(resumePoint: ScenarioResumePoint): void {
    this.progress.resumePoint = normalizeResumePoint(this.progress.resumePoint, resumePoint);
    this.progress.currentScenarioId = this.progress.resumePoint.scenarioId;
  }

  public getResumePoint(): ScenarioResumePoint {
    return { ...this.progress.resumePoint };
  }

  public setCurrentCase(caseId: string, status: CaseStatus = 'investigating'): void {
    this.progress.currentCaseId = caseId;
    addUnique(this.progress.unlockedCaseIds, caseId);
    this.updateCaseStatus(caseId, status);
  }

  public markNodeRead(nodeId: string): void {
    addUnique(this.progress.readNodeIds, nodeId);
  }

  public hasReadNode(nodeId: string): boolean {
    return this.progress.readNodeIds.includes(nodeId);
  }

  public markLineRead(position: ScenarioResumePoint): string {
    const lineKey = createReadLineId(position);
    addUnique(this.progress.readLineIds, lineKey);
    addUnique(this.getCaseScopedReadLineIds(this.progress.currentCaseId), lineKey);
    return lineKey;
  }

  public hasReadLine(position: ScenarioResumePoint): boolean {
    const primaryKey = createReadLineId(position);
    if (this.progress.readLineIds.includes(primaryKey)) {
      return true;
    }

    if (position.lineId) {
      return this.progress.readLineIds.includes(createIndexReadLineId(position));
    }

    return false;
  }

  public appendBacklogLine(
    position: ScenarioResumePoint,
    line: Pick<ScenarioLine, 'speaker' | 'speakerId' | 'text'>
  ): BacklogEntry {
    const normalizedPosition = normalizeResumePoint(this.progress.resumePoint, position);
    const entry: BacklogEntry = {
      id: createBacklogEntryId(normalizedPosition),
      scenarioId: normalizedPosition.scenarioId,
      nodeId: normalizedPosition.nodeId,
      lineIndex: normalizedPosition.lineIndex,
      speaker: line.speaker,
      speakerId: line.speakerId,
      text: line.text
    };

    if (normalizedPosition.lineId) {
      entry.lineId = normalizedPosition.lineId;
    }

    this.progress.backlog = [...this.progress.backlog, entry].slice(-MAX_BACKLOG_ENTRIES);
    return { ...entry };
  }

  public getBacklog(): BacklogEntry[] {
    return this.progress.backlog.map((entry) => ({ ...entry }));
  }

  public addEvidence(evidenceId: string): void {
    addUnique(this.progress.evidenceIds, evidenceId);
    addUnique(this.getCaseScopedEvidenceIds(this.progress.currentCaseId), evidenceId);
  }

  public hasEvidence(evidenceId: string): boolean {
    return this.progress.evidenceIds.includes(evidenceId);
  }

  public updateCaseStatus(caseId: string, status: CaseStatus): void {
    this.progress.caseStatuses[caseId] = status;
    if (status !== 'hidden') {
      addUnique(this.progress.unlockedCaseIds, caseId);
    }
  }

  public getCaseStatus(caseId: string): CaseStatus {
    return this.progress.caseStatuses[caseId] ?? 'hidden';
  }

  public isCaseUnlocked(caseId: string): boolean {
    return (
      this.progress.unlockedCaseIds.includes(caseId) || this.getCaseStatus(caseId) !== 'hidden'
    );
  }

  public updateSettings(settings: Partial<GameSettings>): void {
    this.settings = normalizeSettings(this.settings, settings);
  }

  public resetProgress(config: GameConfig = GAME_CONFIG): void {
    this.progress = createInitialProgress(config);
  }

  public hydrate(snapshot: GameStateHydration): void {
    if (snapshot.progress) {
      this.progress = normalizeProgress(this.progress, snapshot.progress);
    }

    if (snapshot.settings) {
      this.settings = normalizeSettings(this.settings, snapshot.settings);
    }
  }

  private getCaseScopedEvidenceIds(caseId: string): string[] {
    this.progress.evidenceIdsByCase[caseId] ??= [];
    return this.progress.evidenceIdsByCase[caseId];
  }

  private getCaseScopedReadLineIds(caseId: string): string[] {
    this.progress.readLineIdsByCase[caseId] ??= [];
    return this.progress.readLineIdsByCase[caseId];
  }
}

function createInitialProgress(config: GameConfig): GameProgress {
  return {
    schemaVersion: GAME_PROGRESS_SCHEMA_VERSION,
    currentScenarioId: config.initialScenarioId,
    currentCaseId: config.initialCaseId,
    resumePoint: {
      scenarioId: config.initialScenarioId,
      nodeId: config.initialScenarioId,
      lineIndex: 0
    },
    unlockedCaseIds: [config.initialCaseId],
    readNodeIds: [],
    readLineIds: [],
    readLineIdsByCase: {
      [config.initialCaseId]: []
    },
    backlog: [],
    evidenceIds: [],
    evidenceIdsByCase: {
      [config.initialCaseId]: []
    },
    flagsByCase: {},
    caseStatuses: {
      [config.initialCaseId]: 'hidden'
    },
    audioState: {
      currentBgmId: null
    },
    lastSceneType: 'title'
  };
}

function normalizeProgress(base: GameProgress, progress: GameProgressHydration): GameProgress {
  const currentScenarioId = nonEmptyString(progress.currentScenarioId, base.currentScenarioId);
  const currentCaseId = nonEmptyString(progress.currentCaseId, base.currentCaseId);
  const baseResumePoint = {
    ...base.resumePoint,
    scenarioId: currentScenarioId,
    nodeId: currentScenarioId
  };

  return {
    schemaVersion: GAME_PROGRESS_SCHEMA_VERSION,
    currentScenarioId,
    currentCaseId,
    resumePoint: normalizeResumePoint(baseResumePoint, progress.resumePoint),
    unlockedCaseIds: normalizeUnlockedCaseIds(
      progress.unlockedCaseIds,
      progress.caseStatuses,
      base,
      currentCaseId
    ),
    readNodeIds: uniqueStrings(progress.readNodeIds, base.readNodeIds),
    readLineIds: uniqueStrings(progress.readLineIds, base.readLineIds),
    readLineIdsByCase: normalizeStringArrayRecord(
      progress.readLineIdsByCase,
      base.readLineIdsByCase
    ),
    backlog: normalizeBacklog(progress.backlog, base.backlog),
    evidenceIds: uniqueStrings(progress.evidenceIds, base.evidenceIds),
    evidenceIdsByCase: normalizeStringArrayRecord(
      progress.evidenceIdsByCase,
      base.evidenceIdsByCase
    ),
    flagsByCase: normalizeFlagsByCase(progress.flagsByCase, base.flagsByCase),
    caseStatuses: normalizeCaseStatuses(base.caseStatuses, progress.caseStatuses),
    audioState: normalizeAudioState(progress.audioState, base.audioState),
    lastSceneType: nonEmptyString(progress.lastSceneType, base.lastSceneType)
  };
}

function normalizeSettings(base: GameSettings, settings: GameSettingsHydration): GameSettings {
  return {
    masterVolume: clamp01(settings.masterVolume, base.masterVolume),
    bgmVolume: clamp01(settings.bgmVolume, base.bgmVolume),
    seVolume: clamp01(settings.seVolume, base.seVolume),
    voiceVolume: clamp01(settings.voiceVolume, base.voiceVolume),
    textSpeed: clamp01(settings.textSpeed, base.textSpeed),
    autoSpeed: clamp01(settings.autoSpeed, base.autoSpeed),
    skipReadText: booleanOr(settings.skipReadText, base.skipReadText),
    reduceMotion: booleanOr(settings.reduceMotion, base.reduceMotion),
    lightweightEffects: booleanOr(settings.lightweightEffects, base.lightweightEffects),
    screenShake: booleanOr(settings.screenShake, base.screenShake),
    visualEffects: booleanOr(settings.visualEffects, base.visualEffects)
  };
}

function cloneProgress(progress: GameProgress): GameProgress {
  return {
    schemaVersion: GAME_PROGRESS_SCHEMA_VERSION,
    currentScenarioId: progress.currentScenarioId,
    currentCaseId: progress.currentCaseId,
    resumePoint: { ...progress.resumePoint },
    unlockedCaseIds: [...progress.unlockedCaseIds],
    readNodeIds: [...progress.readNodeIds],
    readLineIds: [...progress.readLineIds],
    readLineIdsByCase: cloneStringArrayRecord(progress.readLineIdsByCase),
    backlog: progress.backlog.map((entry) => ({ ...entry })),
    evidenceIds: [...progress.evidenceIds],
    evidenceIdsByCase: cloneStringArrayRecord(progress.evidenceIdsByCase),
    flagsByCase: cloneFlagsByCase(progress.flagsByCase),
    caseStatuses: { ...progress.caseStatuses },
    audioState: { ...progress.audioState },
    lastSceneType: progress.lastSceneType
  };
}

function addUnique(target: string[], value: string): void {
  if (!target.includes(value)) {
    target.push(value);
  }
}

function uniqueStrings(values: unknown, fallback: string[]): string[] {
  if (!Array.isArray(values)) {
    return [...fallback];
  }

  return [...new Set(values.filter((value): value is string => value.length > 0))];
}

function normalizeUnlockedCaseIds(
  unlockedCaseIds: unknown,
  caseStatuses: unknown,
  base: GameProgress,
  currentCaseId: string
): string[] {
  const unlocked = uniqueStrings(unlockedCaseIds, base.unlockedCaseIds);
  const statuses = normalizeCaseStatuses(base.caseStatuses, caseStatuses);
  for (const [caseId, status] of Object.entries(statuses)) {
    if (status !== 'hidden') {
      addUnique(unlocked, caseId);
    }
  }
  addUnique(unlocked, currentCaseId);
  return unlocked;
}

function normalizeStringArrayRecord(
  values: unknown,
  fallback: Record<string, string[]>
): Record<string, string[]> {
  if (!isStringRecord(values)) {
    return cloneStringArrayRecord(fallback);
  }

  const normalized: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(values)) {
    if (key.length > 0) {
      normalized[key] = uniqueStrings(value, []);
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : cloneStringArrayRecord(fallback);
}

function cloneStringArrayRecord(values: Record<string, string[]>): Record<string, string[]> {
  return Object.fromEntries(Object.entries(values).map(([key, items]) => [key, [...items]]));
}

function normalizeFlagsByCase(
  values: unknown,
  fallback: Record<string, Record<string, boolean | number | string>>
): Record<string, Record<string, boolean | number | string>> {
  if (!isStringRecord(values)) {
    return cloneFlagsByCase(fallback);
  }

  const normalized: Record<string, Record<string, boolean | number | string>> = {};
  for (const [caseId, flags] of Object.entries(values)) {
    if (!isStringRecord(flags)) {
      continue;
    }

    const caseFlags: Record<string, boolean | number | string> = {};
    for (const [flagId, flagValue] of Object.entries(flags)) {
      if (
        flagId.length > 0 &&
        (typeof flagValue === 'boolean' ||
          typeof flagValue === 'number' ||
          typeof flagValue === 'string')
      ) {
        caseFlags[flagId] = flagValue;
      }
    }

    if (Object.keys(caseFlags).length > 0) {
      normalized[caseId] = caseFlags;
    }
  }

  return normalized;
}

function cloneFlagsByCase(
  values: Record<string, Record<string, boolean | number | string>>
): Record<string, Record<string, boolean | number | string>> {
  return Object.fromEntries(
    Object.entries(values).map(([caseId, flags]) => [caseId, { ...flags }])
  );
}

function normalizeAudioState(
  value: unknown,
  fallback: GameProgress['audioState']
): GameProgress['audioState'] {
  if (!isStringRecord(value)) {
    return { ...fallback };
  }

  return {
    currentBgmId:
      typeof value.currentBgmId === 'string' && value.currentBgmId.length > 0
        ? value.currentBgmId
        : null
  };
}

function normalizeResumePoint(
  base: ScenarioResumePoint,
  resumePoint: unknown
): ScenarioResumePoint {
  if (!isStringRecord(resumePoint)) {
    return { ...base };
  }

  const normalized: ScenarioResumePoint = {
    scenarioId: nonEmptyString(resumePoint.scenarioId, base.scenarioId),
    nodeId: nonEmptyString(resumePoint.nodeId, base.nodeId),
    lineIndex: nonNegativeInteger(resumePoint.lineIndex, base.lineIndex)
  };

  if (typeof resumePoint.lineId === 'string' && resumePoint.lineId.length > 0) {
    normalized.lineId = resumePoint.lineId;
  }

  return normalized;
}

function normalizeBacklog(values: unknown, fallback: BacklogEntry[]): BacklogEntry[] {
  if (!Array.isArray(values)) {
    return fallback.map((entry) => ({ ...entry }));
  }

  const entries: BacklogEntry[] = [];
  for (const value of values) {
    const entry = normalizeBacklogEntry(value);
    if (entry) {
      entries.push(entry);
    }
  }

  return entries.slice(-MAX_BACKLOG_ENTRIES);
}

function normalizeBacklogEntry(value: unknown): BacklogEntry | null {
  if (!isStringRecord(value)) {
    return null;
  }

  if (
    !isNonEmptyString(value.id) ||
    !isNonEmptyString(value.scenarioId) ||
    !isNonEmptyString(value.nodeId) ||
    !isNonEmptyString(value.text)
  ) {
    return null;
  }

  const entry: BacklogEntry = {
    id: value.id,
    scenarioId: value.scenarioId,
    nodeId: value.nodeId,
    lineIndex: nonNegativeInteger(value.lineIndex, 0),
    text: value.text
  };

  if (isNonEmptyString(value.lineId)) {
    entry.lineId = value.lineId;
  }
  if (isNonEmptyString(value.speaker)) {
    entry.speaker = value.speaker;
  }
  if (isNonEmptyString(value.speakerId)) {
    entry.speakerId = value.speakerId;
  }

  return entry;
}

function createReadLineId(position: ScenarioResumePoint): string {
  const lineIdentity = position.lineId ? `line:${position.lineId}` : `index:${position.lineIndex}`;
  return `${position.scenarioId}/${position.nodeId}/${lineIdentity}`;
}

function createIndexReadLineId(position: ScenarioResumePoint): string {
  return `${position.scenarioId}/${position.nodeId}/index:${position.lineIndex}`;
}

function createBacklogEntryId(position: ScenarioResumePoint): string {
  return createReadLineId(position);
}

function normalizeCaseStatuses(
  base: Record<string, CaseStatus>,
  caseStatuses: unknown
): Record<string, CaseStatus> {
  if (!isStringRecord(caseStatuses)) {
    return { ...base };
  }

  const normalized = { ...base };
  for (const [caseId, status] of Object.entries(caseStatuses)) {
    if (caseId.length > 0 && isCaseStatus(status)) {
      normalized[caseId] = status;
    }
  }

  return normalized;
}

function nonEmptyString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function nonNegativeInteger(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : fallback;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isCaseStatus(value: unknown): value is CaseStatus {
  if (typeof value !== 'string') {
    return false;
  }

  return CASE_STATUSES.has(value as CaseStatus);
}

function booleanOr(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function clamp01(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }

  return Math.min(Math.max(value, 0), 1);
}

function isStringRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
